/**
 * E-Hentai and ExHentai share one forum login (`ipb_member_id` +
 * `ipb_pass_hash`), so Bunko keeps them as twins:
 *
 *   - Saving a logged-in cookie set for either source links the other one
 *     ({@link linkTwinSourceAuth}). ExHentai gets its `igneous` cookie via
 *     its `prepareAuth` hook (the worker's `/eh/exhentai-session`).
 *   - Loading a source that has no saved auth derives it from its twin
 *     ({@link deriveTwinSourceAuth}), which covers accounts that connected
 *     E-Hentai before twinning existed.
 *
 * Only the login identity crosses over. Cloudflare cookies are per-site and
 * stay with the source that owns them.
 *
 * Runtime deps are passed explicitly (see `loadSourceAuth.ts`) so this
 * module stays runnable under `node --test --experimental-strip-types`.
 */

import type { ComicSource, SourceAuth, SourceCookies } from '@/sources/types';

const TWINS: Record<string, string> = { ehentai: 'exhentai', exhentai: 'ehentai' };

/** Cookies that identify one login session; replaced wholesale on re-link. */
const SESSION_COOKIES = new Set(['ipb_member_id', 'ipb_pass_hash', 'igneous', 'sk']);

/** How long a failed derivation is remembered before it is retried. */
const FAILURE_BACKOFF_MS = 10 * 60_000;
/** How long "the twin has no login" is remembered, to avoid repeat lookups. */
const NO_LOGIN_RECHECK_MS = 60_000;

export function twinSourceId(sourceId: string): string | undefined {
  return TWINS[sourceId];
}

/** The shared login identity, or `undefined` for anonymous / incomplete auth. */
export function twinLoginCookies(auth: SourceAuth | undefined): SourceCookies | undefined {
  const memberId = auth?.cookies?.ipb_member_id?.trim();
  const passHash = auth?.cookies?.ipb_pass_hash?.trim();
  if (auth?.anonymous || !memberId || !passHash) return undefined;
  return { ipb_member_id: memberId, ipb_pass_hash: passHash };
}

function sameLogin(a: SourceCookies, b: SourceAuth | undefined): boolean {
  const other = twinLoginCookies(b);
  return (
    other !== undefined &&
    other.ipb_member_id === a.ipb_member_id &&
    other.ipb_pass_hash === a.ipb_pass_hash
  );
}

/**
 * Build the twin's auth from a shared login: keep the twin's own
 * site-specific cookies (Cloudflare), swap in the new login, then let the
 * twin complete it (ExHentai fetches `igneous`). Throws whatever
 * `prepareAuth` throws, e.g. `SadPanda` for accounts without ExHentai access.
 */
export async function buildTwinAuth(
  target: ComicSource,
  login: SourceCookies,
  existing: SourceAuth | undefined,
): Promise<SourceAuth> {
  const cookies: SourceCookies = {};
  for (const [name, value] of Object.entries(existing?.cookies ?? {})) {
    if (!SESSION_COOKIES.has(name)) cookies[name] = value;
  }
  Object.assign(cookies, login);
  const auth: SourceAuth = { cookies };
  return target.prepareAuth ? target.prepareAuth(auth) : auth;
}

export interface TwinSourceAuthDeps {
  getSource: (sourceId: string) => ComicSource | undefined;
  /** Loads saved auth only — must not itself derive from twins. */
  loadAuth: (source: ComicSource) => Promise<SourceAuth | undefined>;
  /** Persists auth (account sync + local). Omit when signed out. */
  saveAuth?: (sourceId: string, auth: SourceAuth) => Promise<void>;
}

export type TwinLinkResult =
  | { status: 'linked'; sourceId: string }
  | { status: 'unchanged'; sourceId: string }
  | { status: 'failed'; sourceId: string; error: unknown }
  | { status: 'not-applicable' };

/**
 * After `sourceId` was saved with `auth`, give its twin the same login.
 * Never throws: the primary save already succeeded, so failures are
 * reported for the caller to surface.
 */
export async function linkTwinSourceAuth(
  sourceId: string,
  auth: SourceAuth,
  deps: TwinSourceAuthDeps & { saveAuth: NonNullable<TwinSourceAuthDeps['saveAuth']> },
): Promise<TwinLinkResult> {
  const twinId = twinSourceId(sourceId);
  const target = twinId ? deps.getSource(twinId) : undefined;
  const login = twinLoginCookies(auth);
  if (!twinId || !target || !login) return { status: 'not-applicable' };
  forgetDerivation(sourceId);
  try {
    const existing = await deps.loadAuth(target).catch(() => undefined);
    if (existing && sameLogin(login, existing)) {
      // Re-check that an existing ExHentai twin still has igneous.
      const completed = target.prepareAuth ? await target.prepareAuth(existing) : existing;
      if (completed === existing) return { status: 'unchanged', sourceId: twinId };
      await deps.saveAuth(twinId, completed);
      return { status: 'linked', sourceId: twinId };
    }
    const twinAuth = await buildTwinAuth(target, login, existing);
    await deps.saveAuth(twinId, twinAuth);
    forgetDerivation(twinId);
    return { status: 'linked', sourceId: twinId };
  } catch (error) {
    return { status: 'failed', sourceId: twinId, error };
  }
}

const derivedAuth = new Map<string, SourceAuth>();
const derivationFailures = new Map<string, number>();
const derivationsInFlight = new Map<string, Promise<SourceAuth | undefined>>();

function forgetDerivation(sourceId: string): void {
  for (const key of [...derivedAuth.keys(), ...derivationFailures.keys()]) {
    if (key.startsWith(`${sourceId}:`)) {
      derivedAuth.delete(key);
      derivationFailures.delete(key);
    }
  }
}

/**
 * For a source with no saved auth, derive it from the twin's login and
 * persist it when possible. Returns `undefined` when there is nothing to
 * derive from or derivation failed (failures back off for ten minutes so
 * page loads don't keep hitting the worker).
 */
export async function deriveTwinSourceAuth(
  source: ComicSource,
  deps: TwinSourceAuthDeps,
): Promise<SourceAuth | undefined> {
  const twinId = twinSourceId(source.id);
  const twin = twinId ? deps.getSource(twinId) : undefined;
  if (!twin) return undefined;
  const noLoginKey = `${source.id}:none`;
  const checkedAt = derivationFailures.get(noLoginKey);
  if (checkedAt !== undefined && Date.now() - checkedAt < NO_LOGIN_RECHECK_MS) return undefined;
  const login = twinLoginCookies(await deps.loadAuth(twin).catch(() => undefined));
  if (!login) {
    derivationFailures.set(noLoginKey, Date.now());
    return undefined;
  }

  const key = `${source.id}:${login.ipb_member_id}:${login.ipb_pass_hash}`;
  const cached = derivedAuth.get(key);
  if (cached) return cached;
  const failedAt = derivationFailures.get(key);
  if (failedAt !== undefined && Date.now() - failedAt < FAILURE_BACKOFF_MS) return undefined;
  const pending = derivationsInFlight.get(key);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const auth = await buildTwinAuth(source, login, undefined);
      derivedAuth.set(key, auth);
      derivationFailures.delete(key);
      await deps.saveAuth?.(source.id, auth).catch(() => undefined);
      return auth;
    } catch {
      derivationFailures.set(key, Date.now());
      return undefined;
    } finally {
      derivationsInFlight.delete(key);
    }
  })();
  derivationsInFlight.set(key, promise);
  return promise;
}

/** Test hook: clear the in-memory derivation caches. */
export function __resetTwinSourceAuthForTests(): void {
  derivedAuth.clear();
  derivationFailures.clear();
  derivationsInFlight.clear();
}
